import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Layers, Plus } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";

import DataTable from "@/components/mui/DataTable";

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [totalCategories, setTotalCategories] = useState(0);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // paginação
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // filtro de busca (name)
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    fetchCategories(0, rowsPerPage, "");
  }, []);

  const fetchCategories = async (
    pageIndex = 0,
    perPage = rowsPerPage,
    searchTerm = search
  ) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/categories`, {
        params: {
          page: pageIndex + 1,
          per_page: perPage,
          search: searchTerm || undefined,
        },
      });

      const data = response.data;
      if (data && data.data) {
        setCategories(data.data);
        setTotalCategories(data.total || data.data.length);
      } else {
        const list = data || [];
        setCategories(list);
        setTotalCategories(list.length);
      }
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || "",
        description: category.description || "",
      });
    } else {
      setEditingCategory(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          formData
        );
        toast.success("Categoria atualizada com sucesso!");
      } else {
        await axios.post(`${API}/categories`, formData);
        toast.success("Categoria criada com sucesso!");
      }

      handleCloseModal();
      fetchCategories(page, rowsPerPage, search);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Erro ao salvar categoria"
      );
    }
  };

  const handleDelete = async (category) => {
    const confirm = window.confirm(
      `Deseja realmente excluir a categoria "${category.name}"?`
    );
    if (!confirm) return;

    try {
      await axios.delete(`${API}/categories/${category.id}`);
      toast.success("Categoria excluída com sucesso!");
      fetchCategories(page, rowsPerPage, search);
    } catch (error) {
      // aqui provavelmente vem o 422 com a mensagem:
      // "Não é possível excluir. Há produtos vinculados a esta categoria."
      toast.error(
        error.response?.data?.message ||
        "Erro ao excluir categoria"
      );
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchCategories(newPage, rowsPerPage, search);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchCategories(0, newRows, search);
  };

  const handleSearch = () => {
    setPage(0);
    fetchCategories(0, rowsPerPage, search);
  };

  if (loading && categories.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const columns = [
    {
      id: "name",
      label: "Nome",
      render: (category) => (
        <Typography fontWeight={600}>{category.name}</Typography>
      ),
    },
    {
      id: "description",
      label: "Descrição",
      render: (category) => category.description || "-",
    },
    {
      id: "products_count",
      label: "Produtos vinculados",
      align: "center",
      render: (category) => category.products_count ?? 0,
    },
    {
      id: "actions",
      label: "Ações",
      align: "right",
      render: (category) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleOpenModal(category)}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDelete(category)}
          >
            Excluir
          </Button>
        </Box>
      ),
    },
  ];

  const tablePagination = {
    page,
    rowsPerPage,
    total: totalCategories,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50],
  };

  return (
    <Box data-testid="categories-page">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={4}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Layers size={32} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Categorias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Organização das categorias de produtos
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenModal(null)}
          data-testid="add-category-btn"
        >
          Nova Categoria
        </Button>
      </Box>

      {/* Filtro de busca */}
      <Paper className="data-table-container">
        <Box
          className="table-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            gap: 2,
            px: 3,
            py: 2,
          }}
        >
          <TextField
            label="Buscar por nome"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ minWidth: 140 }}
          >
            Buscar
          </Button>
        </Box>

        {categories.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <Layers size={64} />
            <Typography variant="h6">
              Nenhuma categoria cadastrada
            </Typography>
            <Typography variant="body2">
              Adicione categorias para organizar seus produtos.
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={categories}
            loading={loading}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `category-row-${row.id}`}
          />
        )}
      </Paper>

      {/* Dialog de criação/edição */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingCategory ? "Editar Categoria" : "Nova Categoria"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />

              <TextField
                label="Descrição"
                fullWidth
                multiline
                minRows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCloseModal}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="contained">
                {editingCategory ? "Atualizar" : "Criar"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
